import type {
  WorkflowDraftDefinition,
  WorkflowPendingChangesSave,
  WorkflowPhaseDef,
  WorkflowReplace,
  WorkflowStatus,
  WorkflowStepDef,
  WorkflowTransitionDef,
} from '@/lib/api/generated/model';

import { stepDisplayOrder } from './layout';
import { UNASSIGNED_PHASE_ID } from './types';
import type { DraftStep, WorkflowDraft } from './types';

/**
 * Fields carried straight from the loaded document because the builder has no
 * UI for them.
 *
 * **Adding a field here is the only way to extend passthrough.** The temptation
 * is to spread `draft.loaded` and be done, and it is wrong twice over:
 *
 *  1. `WorkflowView` and `WorkflowReplace` are different shapes. The view
 *     carries `id`, `key`, `display_order`, `created_at`, `start_step`, and
 *     `done_step`; the replace body is `extra="forbid"` and has none of them.
 *     A spread 422s on *every* save.
 *  2. A spread is silent about what it carries. This list is the record of
 *     which fields the builder is knowingly preserving, and it is what a
 *     reviewer reads when the service grows a new one.
 *
 * The echo is literal: the service's replace door takes `inputs` in the same
 * shape the view returns it (service research D18), so nothing is translated.
 */
const PASSTHROUGH_FIELDS = ['inputs'] as const;

/**
 * The draft as a definition: what both write doors share (spec 127), and what
 * the browser backup keeps (spec 144).
 *
 * Built field by field, for the reasons `PASSTHROUGH_FIELDS` gives.
 */
export function toDraftDefinition(
  draft: WorkflowDraft,
): WorkflowDraftDefinition {
  return {
    name: draft.name,
    description: draft.description,
    // The workflow-wide override (spec 091).
    image: draft.image,
    phases: submittedPhases(draft).map((phase) =>
      toPhaseDef(phase, draft.layout),
    ),
    start: draft.start,
    // Re-wrapped into the envelope the service stores. Always sent, even when
    // empty: omitting it would read as "no opinion", and the service would keep
    // an arrangement the user has just cleared.
    layout: { nodes: draft.layout },
    // Field by field above; the allowlist below. Never a spread.
    ...passthrough(draft),
  };
}

/**
 * The draft as a `PUT` body.
 *
 * `targetStatus` is what separates Save from Publish on a workflow never
 * published — one request shape, one endpoint, and the only difference is
 * which validation tier the service holds the document to.
 */
export function toReplaceRequest(
  draft: WorkflowDraft,
  targetStatus: WorkflowStatus,
): WorkflowReplace {
  return {
    ...toDraftDefinition(draft),
    status: targetStatus,
    expected_version: draft.version,
  };
}

/**
 * The draft as a published workflow's pending changes (spec 144).
 *
 * Two anchors, answering different questions. The revision is the pending
 * changes the author loaded — `null` when there were none, so a colleague's
 * save since is refused rather than overwritten. The version is the published
 * definition they started from, checked by the service on that first save.
 */
export function toPendingChangesSave(
  draft: WorkflowDraft,
): WorkflowPendingChangesSave {
  return {
    ...toDraftDefinition(draft),
    expected_revision: draft.pending?.revision ?? null,
    expected_version: draft.version,
  };
}

/**
 * Where Save sends the draft (spec 144 plan, "Reading and writing").
 *
 * A workflow never published is its own draft, and saves through the replace
 * door keeping that status, as it always has (FR-012). A published one saves
 * into its pending changes, so nothing a board or a running task reads moves
 * until someone presses Publish changes (FR-003).
 */
export type SaveRequest =
  | { door: 'replace'; body: WorkflowReplace }
  | { door: 'pending-changes'; body: WorkflowPendingChangesSave };

export function toSaveRequest(draft: WorkflowDraft): SaveRequest {
  return draft.status === 'published'
    ? { door: 'pending-changes', body: toPendingChangesSave(draft) }
    : { door: 'replace', body: toReplaceRequest(draft, draft.status) };
}

/**
 * The phases as the service should see them: never an empty holding pen.
 *
 * `draftOps` already drops the pen as its last step leaves, so within a session
 * this filters nothing. It is here for the documents that were saved *before*
 * it did — an empty `$unassigned` is a legal thing for the service to have
 * stored on a draft, and every one of them is a workflow that refuses to
 * publish with "Phase id '$unassigned' uses the reserved '$' prefix" (V-12).
 * Their authors cannot repair that from the canvas: the phase they would have
 * to empty is already empty, and the strip does not draw it.
 *
 * A pen that *holds* steps is submitted as it stands. Dropping it would delete
 * those steps to make the publish succeed — silently, and only for the user who
 * had not got round to placing them, which is the one outcome worse than
 * refusing the publish. The service names each one instead (V-16), and the
 * problem list points at it.
 */
function submittedPhases(draft: WorkflowDraft): WorkflowDraft['phases'] {
  return draft.phases.filter(
    (phase) => phase.id !== UNASSIGNED_PHASE_ID || phase.steps.length > 0,
  );
}

function passthrough(
  draft: WorkflowDraft,
): Pick<WorkflowDraftDefinition, 'inputs'> {
  const carried = {} as Pick<WorkflowDraftDefinition, 'inputs'>;
  for (const field of PASSTHROUGH_FIELDS) {
    carried[field] = draft.loaded[field];
  }
  return carried;
}

/**
 * Document order comes from the canvas (FR-017).
 *
 * The board reads phases and steps in document order, so where a user drags a
 * node is what decides the column order they see later. Sorting here rather
 * than keeping the array ordered means the draft's arrays can stay in whatever
 * order edits produced.
 */
function toPhaseDef(
  phase: WorkflowDraft['phases'][number],
  layout: WorkflowDraft['layout'],
): WorkflowPhaseDef {
  // Ordered by where the steps actually sit. Offsets are band-relative, so
  // comparing two steps of the same phase is comparing like with like — which
  // is why the whole layout can be passed without one phase's coordinates
  // affecting another's order.
  const order = stepDisplayOrder(phase.steps, layout);
  const byId = new Map(phase.steps.map((step) => [step.id, step]));

  return {
    id: phase.id,
    name: phase.name,
    steps: order
      .map((id) => byId.get(id))
      .filter((step): step is DraftStep => step !== undefined)
      .map(toStepDef),
  };
}

function toStepDef(step: DraftStep): WorkflowStepDef {
  return {
    id: step.id,
    name: step.name,
    type: step.type,
    prompt: step.prompt,
    llm: step.llm,
    // An override on a human gate is refused by the service, and the draft
    // already drops it on type change — asserted again here because this is
    // the last point before the wire, the same rule `mode` follows below.
    image: step.type === 'ai_step' ? step.image : null,
    // A mode on an AI step is refused by the service, and the draft already
    // drops it on type change — asserted again here because this is the last
    // point before the wire.
    mode: step.type === 'human_gate' ? step.mode : null,
    // The id, not the person: `assignee` is a rendering convenience the service
    // resolves for itself, and sending it back would invite the two to disagree.
    assignee_user_id: step.type === 'human_gate' ? step.assigneeUserId : null,
    // Asserted again here for the same reason `mode` is: the draft already
    // drops it on type change, but this is the last point before the wire.
    workflow: step.type === 'linked_workflow' ? step.workflow : null,
    // Left out until an author picks one, so the service keeps its default.
    start_children:
      step.type === 'linked_workflow' ? (step.startChildren ?? null) : null,
    // Blank is none: the service keeps its built-in prompt alone (spec 183).
    scheduling_instructions:
      step.type === 'linked_workflow'
        ? step.schedulingInstructions?.trim() || null
        : null,
    transitions: step.transitions.map(toTransitionDef),
  };
}

function toTransitionDef(
  transition: WorkflowDraft['phases'][number]['steps'][number]['transitions'][number],
): WorkflowTransitionDef {
  return {
    key: transition.key,
    to: transition.to,
    when: transition.when,
    label: transition.label,
    traversal_limit: transition.traversalLimit,
  };
}
